import React from 'preact/compat';
import { useMemo } from 'preact/hooks';

import { Box, Button, Stack, Typography, Menu, MenuItem } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DownloadIcon from '@mui/icons-material/GetAppRounded';
import CodeIcon from '@mui/icons-material/Code';
import BuildIcon from '@mui/icons-material/Build';
import PreviewIcon from '@mui/icons-material/FindInPageRounded';
import DiffIcon from '@mui/icons-material/SwapHorizRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tooltip from '@mui/material/Tooltip';

import colors from '../colors';
import {
    useGenerate,
    useGenerationLoading,
    useGenerationContents,
    useGenerationDiff,
    useGenerationCommand,
    useClearGeneration,
    GENERATION_TYPE_ZIP,
    GENERATION_TYPE_COMMANDS,
    GENERATION_TYPE_PREVIEW,
    GENERATION_TYPE_DIFF
} from '../../state/generationStore';
import LoadingIcon from '../icons/LoadingIcon';
import { useAdvancedMode } from '../../state/advancedModeStore';

const LauncherGenerate = () => {
    const handleGenerate = useGenerate();
    const loading = useGenerationLoading();
    const contents = useGenerationContents();
    const isDiff = useGenerationDiff();
    const isCommand = useGenerationCommand();
    const clearGeneration = useClearGeneration();

    const [advancedMode, ] = useAdvancedMode();

    const [anchorEl, setAnchorEl] = React.useState(null);
    const isOpen = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleCommand = () => {
        handleClose();
        handleGenerate(GENERATION_TYPE_COMMANDS);
    };
    const handlePreview = () => {
        handleClose();
        handleGenerate(GENERATION_TYPE_PREVIEW);
    };
    const handleDiff = () => {
        handleClose();
        handleGenerate(GENERATION_TYPE_DIFF);
    };
      
    const LauncherPreview = useMemo(() => React.lazy(() => import('../LauncherPreview')), []);

    return (
        <>
        <React.Suspense fallback={<Box sx={{display: 'none'}} />}>
            <section>
                <LauncherPreview isDiff={isDiff} isCommand={isCommand} contents={contents} clearContents={clearGeneration} />
            </section>
        </React.Suspense>
        <Stack
            direction="row"
            spacing="0"
            sx={{
                borderRadius: '4px',
                overflow: 'hidden',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'relative',
                '.MuiButtonBase-root': {
                    color: colors.primaryText,
                    borderRadius: '4px',
                    textTransform: 'capitalize',
                    fontWeight: 600,
                    height: '40px',
                    backgroundColor: colors.action,
                    '&:hover': {
                        backgroundColor: colors.actionHover
                    },
                    '&.Mui-disabled': {
                        cursor: 'progress',
                        pointerEvents: 'all',
                        color: colors.primaryText,
                        backgroundColor: colors.actionDisabled
                    }
                }
            }}
        >
            {advancedMode &&
            <Button
                onClick={handlePreview}
                disabled={loading}
                sx={{
                    padding: '0 60px',
                    width: '256px',
                }}
            >
                { loading ?
                    <LoadingIcon sx={{ fontSize: '20px' }} /> :
                    <PreviewIcon sx={{ fontSize: '20px' }} /> }
                <Typography sx={{ flexGrow: 1, marginTop: '2px' }}> Code Preview </Typography>
            </Button> 
            }
            {advancedMode &&    
            <Tooltip
                title={"Differences between the code for the features selected versus the baseline (no features selected)"}
                placement="top-start"
                componentsProps={{
                    tooltip: {
                        sx: {
                            '&.MuiTooltip-tooltip.MuiTooltip-tooltipPlacementTop': {
                                margin: '0 0 5px',
                                padding: '2px 10px 1px',
                                lineHeight: '14px',
                                maxWidth: '236px'
                            },
                            backgroundColor: colors.tooltipBackground,
                            color: colors.tooltipText,
                            border: 'none'
                        }
                    }
                }}
            >         
                <Button
                    onClick={handleDiff}
                    disabled={loading}
                    sx={{
                        padding: '0 60px',
                        width: '256px',
                        display: 'flex',
                        flexDirection: 'row',
                    }}
                >
                    { loading ?
                        <LoadingIcon sx={{ fontSize: '20px' }} /> :
                        <DiffIcon sx={{ fontSize: '20px' }} /> }
                    <Typography sx={{ flexGrow: 1, marginTop: '2px' }}> Differences </Typography>
                </Button>
            </Tooltip> 
            }
            {!advancedMode &&
            <IconButton
                id="generate-project-button"
                variant="contained"
                onClick={() => handleGenerate(GENERATION_TYPE_ZIP)}
                disabled={loading}
                sx={{
                    padding: '0 12px',
                    width: '200px',
                    display: 'flex',
                    flexDirection: 'row',
                }}
            >
                { loading ?
                    <LoadingIcon sx={{ fontSize: '20px' }} /> :
                    <DownloadIcon sx={{ fontSize: '20px' }} /> }
                <Typography sx={{ flexGrow: 1, marginTop: '2px'}}> Generate Project </Typography>
            </IconButton>
            }
            {advancedMode &&
            <IconButton
                id="expand-options-button"
                aria-controls={isOpen ? 'options' : undefined}
                aria-haspopup="true"
                aria-expanded={isOpen ? 'true' : undefined}
                variant="contained"
                disableElevation
                onClick={handleClick}
                disabled={loading}
                sx={{
                    padding: '0 12px',
                    width: '256px',
                    display: 'flex',
                    flexDirection: 'row',
                }}
            >
                { loading ?
                    <LoadingIcon sx={{ fontSize: '20px' }} /> :
                    <BuildIcon sx={{ fontSize: '20px', paddingLeft: '28px' }} /> }
                <Typography sx={{ flexGrow: 1, marginTop: '2px'}}> Generate Project </Typography>
                <ExpandMoreIcon sx={{paddingLeft: '20px'}} />
            </IconButton>
            }
            {advancedMode &&
            <Menu
                id='options'
                MenuListProps={{
                    'aria-labelledby': 'expand-options-button'
                }}
                anchorEl={anchorEl}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                anchorPosition={{top: '10px'}}
                PaperProps={{
                    style: {
                        transform: 'translateY(8px)'
                    }
                }}

                open={isOpen}
                onClose={handleClose}
                sx={{
                    "& .MuiPaper-root": {
                        backgroundColor: loading ? colors.actionDisabled : colors.action,
                        color: colors.primaryText,
                        textTransform: 'capitalize',
                        fontWeight: 600,
                        borderRadius: '4px',
                        width: '170px',
                    }
                }}
            >
                <MenuItem onClick={() => handleGenerate(GENERATION_TYPE_ZIP)} sx={{ '&:hover': {backgroundColor: colors.actionHover} }} disableRipple>
                    <DownloadIcon sx={{ fontSize: "20px", paddingRight: '12px'}} />
                    <Typography sx={{ flexGrow: 1, marginTop: '2px' }}> Download Zip </Typography>
                </MenuItem>
                <MenuItem onClick={handleCommand} sx={{ '&:hover': {backgroundColor: colors.actionHover} }} disableRipple>
                    <CodeIcon sx={{ fontSize: "20px", paddingRight: '12px'}} />
                    <Typography sx={{ flexGrow: 1, marginTop: '2px' }}> Commands </Typography>
                </MenuItem>
            </Menu>
            }
        </Stack>
        </>
    );
};

export default LauncherGenerate;